import React, { useMemo } from "react";
import { FaMinus, FaPlus, FaUndo } from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { chartColors } from "../../../../Config/Colors";
import {
  CURVE_KEYS,
  CURVE_LABELS,
  FALLBACK_PRESET,
  normalizeBounds,
  maxFor,
  validateCurve,
  buildCurveSeries,
} from "./autoMixerCurve";

// Tabletteki "Otomatik Karıştırma" çekmecesinin panel karşılığı.
// - Açık/Kapalı (autoMixer)
// - Eğri (autoMixerParams): null = varsayılan (preset). +/- ile değiştirilince
//   özel eğri olur; "Varsayılan Yap" yeniden null gönderir.
// - Grafik: 0 → pişirme sıcaklığı arası her 1 °C'de ham karıştırıcı hızı.
// Değişiklikler hemen gönderilmez; sayfanın Kaydet akışına (pending) eklenir.

const HW_AXIS = [11, 62]; // tabletteki grafikle aynı dikey ölçek

function Stepper({ k, value, min, max, disabled, onStep }) {
  const { label, unit } = CURVE_LABELS[k];
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5">
      <div className="min-w-0">
        <div className="text-xs font-SemiBold text-gray-700 truncate">{label}</div>
        <div className="text-[10px] text-gray-400">
          {min}–{max} {unit}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          aria-label={`${label} azalt`}
          disabled={disabled || value <= min}
          onClick={() => onStep(k, value - 1)}
          className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 hover:bg-gray-100"
        >
          <FaMinus size={9} />
        </button>
        <span className="w-9 text-center text-sm font-Bold tabular-nums">
          {value}
        </span>
        <button
          type="button"
          aria-label={`${label} artır`}
          disabled={disabled || value >= max}
          onClick={() => onStep(k, value + 1)}
          className="h-7 w-7 flex items-center justify-center rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 hover:bg-gray-100"
        >
          <FaPlus size={9} />
        </button>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.idPrefix        form kontrol id'leri için benzersiz ön ek
 * @param {object} props.autoMixer       READ setting (Checkbox)
 * @param {object} props.autoMixerParams READ setting (AutoMixerCurve)
 * @param {boolean} props.changed        bekleyen değişiklik var mı (rozet)
 * @param {number} props.cookingTemperature grafik x ekseni ucu
 * @param {(key:string, value:any) => void} props.onChange
 * @param {boolean} props.showApplyAll   "Tüm gözlere uygula" görünsün mü
 * @param {boolean} props.applyAll
 * @param {(v:boolean) => void} props.onApplyAllChange
 */
export default function AutoMixerCard({
  idPrefix,
  autoMixer,
  autoMixerParams,
  changed,
  cookingTemperature,
  onChange,
  showApplyAll,
  applyAll,
  onApplyAllChange,
}) {
  const enabled = autoMixer ? autoMixer.value !== false : true;
  const disabled = !!(autoMixer?.disabled || autoMixerParams?.disabled);
  const bounds = normalizeBounds(autoMixerParams?.bounds);
  const preset =
    validateCurve(autoMixerParams?.preset, bounds) || FALLBACK_PRESET;
  const custom = validateCurve(autoMixerParams?.value, bounds);
  const params = custom || preset;
  const ct = Number(cookingTemperature) || 96; // tabletteki yedek değer

  const series = useMemo(() => buildCurveSeries(params, ct), [params, ct]);

  const handleStep = (key, next) => {
    const draft = { ...params, [key]: next };
    // Dirsek hızı tabanın altına inerse taban da iner (tablet kuralı).
    if (key === "stepHw" && draft.floorHw > next) {
      draft.floorHw = Math.max(bounds.floorHw[0], next);
    }
    const valid = validateCurve(draft, bounds);
    if (valid) onChange("autoMixerParams", valid);
  };

  return (
    <div
      className={`rounded-xl border bg-white p-3 col-span-1 sm:col-span-2 lg:col-span-3 ${
        changed ? "border-indigo-400 ring-2 ring-indigo-100" : "border-gray-200"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-sm font-Bold text-gray-800">
          Otomatik Karıştırma
        </span>
        {autoMixerParams && (
          <span
            className={`text-[11px] px-2 py-0.5 rounded-md border ${
              custom
                ? "bg-amber-50 border-amber-300 text-amber-700"
                : "bg-gray-50 border-gray-200 text-gray-500"
            }`}
          >
            {custom ? "Özel eğri" : "Varsayılan eğri"}
          </span>
        )}
        <label className="ml-auto inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            id={`${idPrefix}-autoMixer`}
            type="checkbox"
            className="h-4 w-4"
            checked={enabled}
            disabled={disabled || !autoMixer}
            onChange={(e) => onChange("autoMixer", e.target.checked)}
          />
          {enabled ? "Açık" : "Kapalı"}
        </label>
      </div>

      {!autoMixerParams ? (
        <p className="text-xs text-gray-500">
          Makinenin uygulama sürümü eğri ayarını panele göndermiyor; yalnız
          aç/kapat değiştirilebilir.
        </p>
      ) : (
        <>
          <div className={enabled ? "" : "opacity-50"}>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <XAxis
                    dataKey="temp"
                    type="number"
                    domain={[0, ct]}
                    ticks={[0, params.kneeTemp, ct]}
                    tickFormatter={(v) => `${v}°C`}
                    stroke={chartColors.axis}
                    fontSize={10}
                  />
                  <YAxis
                    domain={HW_AXIS}
                    stroke={chartColors.axis}
                    fontSize={10}
                    width={40}
                  />
                  <Tooltip
                    formatter={(v) => [`${v} hw`, "Hız"]}
                    labelFormatter={(t) => `${t} °C`}
                  />
                  <ReferenceLine
                    x={params.kneeTemp}
                    stroke={chartColors.mixerKnee}
                    strokeDasharray="4 3"
                  />
                  <Bar
                    dataKey="hw"
                    fill={enabled ? chartColors.mixerBar : chartColors.mixerBarOff}
                    isAnimationActive={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Yatay: sıcaklık (0 → pişirme {ct}°C), dikey: ham karıştırıcı
              hızı. Kesik çizgi dirsek sıcaklığı; sonrasında hız basamak
              basamak iner, aralarda taban hızına düşer.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
            {CURVE_KEYS.map((k) => (
              <Stepper
                key={k}
                k={k}
                value={params[k]}
                min={bounds[k][0]}
                max={maxFor(k, params, bounds)}
                disabled={disabled || !enabled}
                onStep={handleStep}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2">
            <button
              type="button"
              disabled={disabled || !enabled || !custom}
              onClick={() => onChange("autoMixerParams", null)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-gray-300 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-100"
            >
              <FaUndo size={11} />
              Ayarları Varsayılan Yap
            </button>
            {showApplyAll && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  id={`${idPrefix}-autoMixer-apply-all`}
                  type="checkbox"
                  className="h-4 w-4"
                  checked={applyAll}
                  disabled={disabled}
                  onChange={(e) => onApplyAllChange(e.target.checked)}
                />
                Bu ayarları tüm gözlere uygula
              </label>
            )}
          </div>
        </>
      )}
    </div>
  );
}
